import { Button, Input, Space } from 'antd';
import { cloneDeep, indexOf, without } from 'lodash';
import isEqual from 'lodash/isEqual';
import React from 'react';

import Icon from '@ant-design/icons';
import PlusIcon from '@icons/emp/plus_icon.svg';
import TreeIcon from '@icons/sankey/tree-icon.svg';
import { IEventJourney } from '@src/emp-3.0/components/Sankey/Configurations/EventJourney/typed';
import {
  CustomDropdownDualSelectWithRequest,
  DeleteIcon,
} from '@src/emp-3.0/components/Sankey/components/CompactDualSelect';
import { DimensionValueSelect } from '@src/emp-3.0/components/Sankey/components/CompactDualSelect/styled';
import { isEventBreakdown, isLandingEvent } from '@src/emp-3.0/components/Sankey/utils/check';
import { IEventBreakdown, IEventFilter, ILandingEvent } from '@src/emp-3.0/types';
import { toLowerCase } from '@src/emp-3.0/utils';
import { addEventNameToFilters } from '@src/emp-3.0/components/Sankey/utils';

const EventJourney: React.FC<IEventJourney> = ({
  eventData,
  interval,
  filters,
  showDimensionTitle = false,
  showDelete = true,
  loading,
  eventOptions = [],
  fixEvent = [],
  compactDualSelectProps = {},
  onDeleteEvent,
  onChange,
  onEventChange,
  onAddDimension,
  addDimensionButtonProps = { show: true, text: 'Add Dimension' },
}) => {
  const {
    dimensionSelectProps,
    dimensionValueSelectProps = {},
    style: compactDualSelectStyle,
    onDelete,
    ...restCompactDualSelectProps
  } = compactDualSelectProps;

  const { mode } = dimensionValueSelectProps;

  const eventFilters: IEventFilter[][] = isLandingEvent(eventData)
    ? eventData?.filters
    : isEventBreakdown(eventData)
    ? eventData?.breakdowns
    : [];

  const isFixBreakdownEvent = fixEvent.length > 0 && isEventBreakdown(fixEvent[0]);

  const dimensionNameOptions = dimensionSelectProps?.options?.map((item) => {
    if (eventFilters.some((andFilter) => andFilter[0].field === item.value)) {
      return {
        ...item,
        disabled: true,
      };
    }
    return item;
  });

  const handleEventChange = (value) => {
    const cloneEventData = cloneDeep(eventData);
    cloneEventData.event_name = value;
    if (isLandingEvent(cloneEventData)) {
      cloneEventData.filters = [];
    }
    onEventChange?.(cloneEventData);
  };

  const handleChange = (value, andIndex) => {
    const cloneEventData: ILandingEvent | IEventBreakdown = cloneDeep(eventData);
    if (isLandingEvent(cloneEventData)) {
      cloneEventData.filters[andIndex] = value;
      onChange?.(cloneEventData);
    } else if (isEventBreakdown(cloneEventData)) {
      cloneEventData.breakdowns[andIndex] = value;
      onChange?.(cloneEventData);
    }
    onDelete?.();
  };

  const handleDelete = (value, removeAndIndex) => {
    const cloneEventData: ILandingEvent | IEventBreakdown = cloneDeep(eventData);
    if (isLandingEvent(cloneEventData)) {
      cloneEventData.filters = cloneEventData.filters.filter(
        (_, index) => index !== removeAndIndex,
      );
      onChange?.(cloneEventData);
    } else if (isEventBreakdown(cloneEventData)) {
      cloneEventData.breakdowns = cloneEventData.breakdowns.filter(
        (_, index) => index !== removeAndIndex,
      );
      onChange?.(cloneEventData);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          style={{
            width: 64,
            background: '#e6edff',
            color: 'rgba(0,0,0,0.88)',
            cursor: 'default',
            borderRadius: '2px',
            flexShrink: 0,
          }}
          value="Event: "
          disabled
        />
        <DimensionValueSelect
          loading={loading}
          value={eventData.event_name}
          optionLabelProp="value"
          options={eventOptions}
          filterOption={(inputValue, option) =>
            toLowerCase(option?.value).includes(toLowerCase(inputValue))
          }
          showSearch
          showRightBorder={!showDelete}
          disabled={isFixBreakdownEvent}
          onChange={(value) => {
            handleEventChange(value);
          }}
        />
        {showDelete && <DeleteIcon onClick={onDeleteEvent} />}
      </Space.Compact>
      {showDimensionTitle && eventFilters.length > 0 && (
        <span
          style={{
            color: 'rgba(0, 0, 0, 0.4)',
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '-0.12px',
            padding: '0 8px',
          }}
        >
          Breakdown Dimension :
        </span>
      )}
      {eventFilters.map((andFilters, andIndex) => {
        const field = andFilters[0].field;
        const value = andFilters[0].value;
        const is_all = andFilters[0].is_all;

        const findEvent = fixEvent.find((event) => event.event_name === eventData.event_name);
        const isFixBreakdownEvent = findEvent && isEventBreakdown(findEvent);

        const notShowDelete = isEventBreakdown(eventData) && isFixBreakdownEvent;

        let disabledValueOptions: string[] = mode === 'multiple' ? [''] : [];
        !is_all &&
          isFixBreakdownEvent &&
          findEvent.breakdowns.forEach((andItem) => {
            if (andItem[0].field === field) {
              const values = andItem[0].value;
              disabledValueOptions = mode === 'multiple' ? ['', ...values] : values;
            }
          });

        const filtersWithBreakdownEvent = addEventNameToFilters(filters, eventData.event_name);
        return (
          <CustomDropdownDualSelectWithRequest
            key={`${field}_${andIndex}`}
            disabledValueOptions={disabledValueOptions}
            interval={interval}
            filters={filtersWithBreakdownEvent}
            showDelete={!notShowDelete}
            allChecked={is_all}
            onAllCheckedChange={(checked) => {
              if (checked) {
                handleChange([{ ...andFilters[0], is_all: checked, value: [] }], andIndex);
              } else {
                handleChange([{ ...andFilters[0], is_all: checked }], andIndex);
              }
            }}
            dimensionSelectProps={{
              value: field,
              ...dimensionSelectProps,
              disabled: notShowDelete || dimensionSelectProps?.disabled,
              showArrow: !notShowDelete || dimensionSelectProps?.showArrow,
              options: dimensionNameOptions,
              onChange: (value, ...rest) => {
                handleChange(
                  [{ ...andFilters[0], field: value, value: [], is_all: mode === 'multiple' }],
                  andIndex,
                );
                dimensionSelectProps?.onChange?.(value, ...rest);
              },
              style: { minWidth: 80, ...dimensionSelectProps?.style },
            }}
            dimensionValueSelectProps={{
              value: value,
              ...dimensionValueSelectProps,
              optionLabelProp: 'value',
              onChange: (value: undefined | string | string[], option) => {
                const updatedValue = typeof value === 'string' ? [value] : value ?? [];
                const isAll = isEqual(updatedValue, ['All Values']);

                const indexAllValues = Array.isArray(value) ? indexOf(value, 'All Values') : -1;
                const valuesWithoutAll = Array.isArray(value)
                  ? without(value, 'All Values')
                  : updatedValue;
                Array.isArray(option) && option.splice(indexAllValues, 1);
                if (isAll) {
                  handleChange([{ ...andFilters[0], value: valuesWithoutAll }], andIndex);
                } else {
                  handleChange(
                    [{ ...andFilters[0], value: valuesWithoutAll, is_all: false }],
                    andIndex,
                  );
                }
                dimensionValueSelectProps?.onChange?.(updatedValue, option);
              },
              style: { ...dimensionValueSelectProps?.style },
            }}
            style={{ paddingLeft: 16, ...compactDualSelectStyle }}
            prefix={
              <Icon
                component={TreeIcon}
                style={{
                  backgroundColor: '#f5f5f5',
                  fontSize: '10px',
                  border: '1px solid #d9d9d9',
                  borderRight: 0,
                  paddingLeft: '8px',
                  display: 'inline-flex',
                }}
              />
            }
            onDelete={() => handleDelete(andFilters, andIndex)}
            {...restCompactDualSelectProps}
          />
        );
      })}
      {addDimensionButtonProps.show && (
        <Button
          type="link"
          disabled={!eventData?.event_name}
          onClick={onAddDimension}
          style={{ width: 154, paddingInlineStart: 0 }}
        >
          <Icon component={PlusIcon} />
          {addDimensionButtonProps?.text ?? 'Add Dimension'}
        </Button>
      )}
    </div>
  );
};

export default EventJourney;
